import React, { useState, useRef, useEffect } from 'react';
import {
  object,
  number,
  array,
  string,
  boolean,
} from 'yup';
import { Formik, Form, useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import uniq from 'lodash/uniq';

import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';

import useHttpStatus from '~/commons/hooks/useHttpStatus';
import { initFormValues } from '~/commons/helpers';
import env from '~/commons/config/env';

import UnloadPrompt from '~/commons/components/UnloadPrompt.jsx';
import FormHttpSaveBtn from '~/commons/components/FormHttpSaveBtn.jsx';
import { useModalAlert } from '../modals/ModalAlert.jsx';
import useTimer from '../../hooks/useTimer';
import Padding from '../layout/Padding.jsx';
import ModalAnsConfirm from '../modals/ModalAnsConfirm.jsx';
import FormAutoSave from './FormAutoSave.jsx';
import FieldsQuestions from './FieldsQuestions.jsx';

const schema = object({
  answers: array().of(
    object({
      answered: number().when('format', {
        is: 'single-select',
        then: number().required(),
      }),
      answeredSeq: array()
        .when('format', {
          is: 'multi-select',
          then: array().of(number()).min(1).required(),
        })
        .when('format', {
          is: 'ordering',
          then: array()
            .of(number().required())
            .required()
            .test('Duplicates', (v) => uniq(v).length === v.length),
        })
        .when('format', {
          is: 'matching',
          then: array()
            .of(number().required())
            .required()
            .test('Duplicates', (v) => uniq(v).length === v.length),
        })
        .when('format', {
          is: 'image-dragdrop',
          then: array()
            .of(number().nullable())
            .min(1)
            .required()
            .test('required-slots-filled', function testRequiredSlotsFilled(v) {
              const requiredSlots = Array.isArray(this.parent.requiredSlots)
                ? this.parent.requiredSlots
                : [];

              if (!Array.isArray(v) || v.length === 0) return false;
              if (requiredSlots.length === 0) {
                return v.every((item) => item !== null && item !== undefined);
              }

              return requiredSlots.every((required, idx) => {
                if (!required) return true;
                return v[idx] !== null && v[idx] !== undefined;
              });
            }),
        })
        .when('format', {
          is: 'sentence-completion',
          then: array()
            .of(string().nullable())
            .min(1)
            .required()
            .test('required-slots-filled', function testRequiredSlotsFilled(v) {
              const requiredSlots = Array.isArray(this.parent.requiredSlots)
                ? this.parent.requiredSlots
                : [];

              if (!Array.isArray(v) || v.length === 0) return false;
              if (requiredSlots.length === 0) {
                return v.every((text) => typeof text === 'string' && text.trim() !== '');
              }

              return requiredSlots.every((required, idx) => {
                if (!required) return true;
                const text = v[idx];
                return typeof text === 'string' && text.trim() !== '';
              });
            }),
        })
        .when('format', {
          is: 'idiom-fill',
          then: array()
            .of(string().nullable())
            .min(1)
            .required()
            .test('required-slots-filled', function testRequiredSlotsFilled(v) {
              const requiredSlots = Array.isArray(this.parent.requiredSlots)
                ? this.parent.requiredSlots
                : [];

              if (!Array.isArray(v) || v.length === 0) return false;
              if (requiredSlots.length === 0) {
                return v.every((text) => typeof text === 'string' && text.trim() !== '');
              }

              return requiredSlots.every((required, idx) => {
                if (!required) return true;
                const text = v[idx];
                return typeof text === 'string' && text.trim() !== '';
              });
            }),
        }),
      requiredSlots: array().of(boolean()),
    }),
  ),
});

const buildRequiredSlots = (q) => {
  if (q.format === 'image-dragdrop') {
    if (Array.isArray(q.answers) && q.answers.length > 0) {
      return q.answers.map((a) => (
        a !== null && typeof a !== 'undefined' && a !== ''
      ));
    }
    if (!Array.isArray(q.matches)) return [];
    return q.matches.map((m) => (m != null && String(m).trim() !== ''));
  }

  if (q.format === 'sentence-completion') {
    if (!Array.isArray(q.answers)) return [];
    return q.answers.map((a) => (
      a !== null && typeof a !== 'undefined' && a !== ''
    ));
  }

  if (q.format === 'idiom-fill') {
    const rows = Array.isArray(q.gridSize) && q.gridSize.length >= 2
      ? Number(q.gridSize[0])
      : 5;
    const cols = Array.isArray(q.gridSize) && q.gridSize.length >= 2
      ? Number(q.gridSize[1])
      : 5;

    const cellExpectedMap = {};
    const idioms = Array.isArray(q.idioms) ? q.idioms : [];

    idioms.forEach((idm) => {
      const start = Array.isArray(idm?.start) ? idm.start : [0, 0];
      const end = Array.isArray(idm?.end) ? idm.end : start;
      const direction = idm?.direction || (start[0] === end[0] ? 'horizontal' : 'vertical');
      const pattern = Array.isArray(idm?.pattern) ? idm.pattern : [];

      if (direction === 'horizontal') {
        const row = start[0];
        const startCol = Math.min(start[1], end[1]);
        for (let i = 0; i < pattern.length; i += 1) {
          const col = startCol + i;
          cellExpectedMap[`${row},${col}`] = pattern[i] || '';
        }
      } else {
        const col = start[1];
        const startRow = Math.min(start[0], end[0]);
        for (let i = 0; i < pattern.length; i += 1) {
          const row = startRow + i;
          cellExpectedMap[`${row},${col}`] = pattern[i] || '';
        }
      }
    });

    const requiredSlots = [];
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        const key = `${row},${col}`;
        if (Object.prototype.hasOwnProperty.call(cellExpectedMap, key)) {
          requiredSlots.push(cellExpectedMap[key] === '');
        }
      }
    }

    return requiredSlots;
  }

  return [];
};

const FormValidationDebug = () => {
  const {
    values,
    errors,
    isValid,
    isSubmitting,
    submitCount,
  } = useFormikContext();

  useEffect(() => {
    const rows = Array.isArray(values?.answers) ? values.answers : [];
    const rowErrors = Array.isArray(errors?.answers) ? errors.answers : [];

    const blocked = rows
      .map((row, index) => ({
        index,
        format: row?.format,
        question: row?.question,
        answered: row?.answered,
        answeredSeq: row?.answeredSeq,
        requiredSlots: row?.requiredSlots,
        rowErrors: rowErrors[index] || null,
      }))
      .filter((r) => r.rowErrors);

    const hasErrors = Object.keys(errors || {}).length > 0;
    if (!hasErrors && isValid) return;

    console.log('[FormAnswer][YupValidation]', {
      isValid,
      isSubmitting,
      submitCount,
      errors,
      blocked,
    });
  }, [values, errors, isValid, isSubmitting, submitCount]);

  return null;
};

const FormAnswer = ({
  answer,
  competition: {
    completedMsg,
    duration, // duration in minutes
    questions,

  },
}) => {
  const history = useHistory();
  const { alert } = useModalAlert();
  const [canLeave, setCanLeave] = useState(false);
  const [doublePoint, setDoublePoint] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const qRef = useRef(null);
  const [submitRes, submitReq] = useHttpStatus();
  const [saveRes, saveReq] = useHttpStatus();
  const defaults = initFormValues({
    answers: questions.map((q) => {
      const { answered, answeredSeq } = answer?.answers?.find((a) => a.question === q._id) || {};

      return {
        question: q._id,
        format: q.format, // just for validation only
        answered,
        challenge: q.challenge,
        requiredSlots: buildRequiredSlots(q),
        answeredSeq:
          answeredSeq
          || (['ordering', 'matching'].includes(q.format)
            ? q.choices.map(() => undefined)
            : []),
      };
    }),
    doublePoint: doublePoint?._id || null,
  });
  let { timeLeft, fmtTimeLeft } = useTimer({
    duration: duration * 1000 * 60,
  });

  if (env.audit) {
    timeLeft = duration * 1000 * 60;
    fmtTimeLeft = `${duration}:00`;
  }

  // intermediate save
  const save = async (values) => {
    if (!env.audit) {
      saveReq({
        url: `api/quiz/answers/${answer._id}`,
        method: 'patch',
        data: values,
      });
    }
  };

  // final submit
  const submit = async (values, timesup = false) => {
    if (env.audit) {
      history.push('/app');

      return;
    }

    // make the request
    const { status, payload } = await submitReq({
      url: `api/quiz/answers/${answer._id}`,
      method: 'patch',
      data: { ...values, completed: true },
    });

    if (status === 'success') {
      // completed msg
      alert(
        <div className="text-secondary">
          你的答案已提交
          <br />
          {completedMsg}
        </div>,
      );
    } else {
      alert(
        <h5 className="text-danger">
          <FA icon={faExclamationCircle} />
          &nbsp;
          {(payload !== '網絡異常'
            && (typeof payload === 'string'
              ? payload
              : JSON.stringify(payload)))
            || '網絡異常，未能提交答案'}
        </h5>,
        'danger',
      );
    }

    if (status === 'success' || timesup) {
      // no confirm
      setCanLeave(true);
      // back to main page
      history.push('/app');
    }
  };

  return (
    <>
      <UnloadPrompt
        when={!canLeave}
        message={`離開當前頁面後無法返回比賽，餘下時間還有${fmtTimeLeft}`}
      />
      <Formik
        validationSchema={schema}
        initialValues={defaults}
        onSubmit={async () => {
          setConfirmModal(true);
        }}
      >
        {({ values, setFieldValue }) => (
          <>
            <FormValidationDebug />
            <FormAutoSave
              onSave={save}
              saveOn={env.audit ? null : 30 * 1000}
              onSubmit={(v) => {
                submit(v, true);
              }}
              submitOn={env.audit ? null : duration * 1000 * 60}
            />
            <ModalAnsConfirm
              timeLeft={fmtTimeLeft}
              show={confirmModal}
              onConfirm={() => {
                setConfirmModal(false);
                submit(values);
              }}
              doublePoint={doublePoint}
              haveChallenge ={questions?.some((q) => q.challenge) || false}
              onReject={() => {
                setConfirmModal(false);

                const empty = values.answers.find(
                  (a) => a.answered === undefined,
                )?.question;

                if (empty) {
                  qRef.current.scrollIntoView(empty);
                }
              }}
            />
            <Form>
              <div
                className={`
                  toast bg-danger text-white
                  pos-fixed-tc mt-3
                  tra-opacity
                  ${saveRes.status === 'error' ? 'show' : ''}
                `}
                style={{
                  zIndex: 1000,
                  pointerEvents: saveRes.status === 'error' ? 'auto' : 'none',
                }}
              >
                <div className="toast-body">
                  <FA icon={faExclamationCircle} />
                  &nbsp;
                  {(() => {
                    const p = saveRes.payload;
                    if (p == null) return '網絡異常';
                    if (typeof p === 'string') return p;
                    try {
                      return JSON.stringify(p);
                    } catch (e) {
                      return String(p);
                    }
                  })()}
                </div>
              </div>

              {/* header */}
              <Padding
                className={`pos-sticky ${
                  timeLeft <= 3 * 60 * 1000
                    ? 'alert-danger'
                    : 'bg-light text-primary'
                }`}
              >
                <div className="row align-items-center">
                  <div className="col-md-6 offset-md-3 py-3 text-center">
                    <h4 className="my-1 text-secondary">
                      餘下時間: {fmtTimeLeft}
                    </h4>
                  </div>
                  <div className="col-3 d-none d-md-block text-right">
                    <FormHttpSaveBtn
                      res={submitRes}
                      className="btn-primary px-3 rounded-pill"
                      onlyValid
                    >
                      提交答案
                    </FormHttpSaveBtn>
                  </div>
                </div>
              </Padding>

              {/* questions */}
              <FieldsQuestions
                ref={qRef}
                questions={questions}
                doublePoint={doublePoint}
                setDoublePoint={(id) => {
                  // keep both local state and Formik field in sync
                  setDoublePoint(id);
                  try {
                    setFieldValue('doublePoint', id);
                  } catch (e) {
                    // ignore if Formik setter unavailable
                  }
                }}
                challenge={questions?.some((q) => q.challenge) || false}
              />

              <div className="bg-white pos-sticky-bottom d-md-none">
                <FormHttpSaveBtn
                  res={submitRes}
                  className="btn-primary btn-block py-3"
                  onlyValid
                >
                  提交答案
                </FormHttpSaveBtn>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
};

export default FormAnswer;
